import React, { useEffect, useMemo, useState } from 'react';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import { GridLayoutElement, GridLayouts } from 'adapter/in/dashboards/components/generic/ReactGrid';
import GridContainer from 'adapter/in/dashboards/containers/Grid';
import { useDashboard } from 'adapter/in/dashboards/context/Dashboard';
import { LAYOUT_STATUS } from 'adapter/in/dashboards/context/EditDashboard';
import { dispatchWidgetsView, useWidgetsView } from 'adapter/in/dashboards/context/Widgets/view';
import Layout, { LayoutElement, LayoutType } from 'domain/entity/layout';
import Widget from 'domain/entity/widget';
import { isSome, Option } from 'fp-ts/lib/Option';
import { useLayouts } from 'adapter/in/dashboards/context/Layouts';
import { dispatchLayoutsView, useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';
import { useReadLayouts } from 'adapter/in/dashboards/hooks/Layouts/useReadLayouts';
import { isMobileOnly, isTablet } from 'react-device-detect';
import AddFirstWidgetResultContainer from 'adapter/in/dashboards/containers/EditDashboard/AddFirstWidgetResult';
import EmptyDashboardPage from 'adapter/in/dashboards/pages/Dashboard/EmptyDashboardPage';
import { Loading, Result, ResultVariants } from '@dexma/ui-components';
import i18n from 'asset/translation';

export type ViewSelectorContainerProps = {
    isEditMode: boolean;
    isEditingLayout: boolean;
    dashboardId: string;
    disableRedirect?: boolean;
};

export type UseGridContainerReturn = {
    gridLayouts: GridLayouts;
    allGridLayouts: GridLayouts;
    widgetMap: Map<string, Widget>;
    setAllGridLayouts: React.Dispatch<React.SetStateAction<GridLayouts>>;
};

export const useGridLayoutsContainer = (
    editingLayout: boolean,
    isEditEnabled: boolean,
    readLayoutsResult: Option<Layout[]>
): UseGridContainerReturn => {
    const { resetWidgets } = dispatchWidgetsView();
    const [allGridLayouts, setAllGridLayouts] = useState<GridLayouts>({});
    const { widgets } = useWidgetsView();

    const { selectedLayoutType } = useLayoutsView();

    const filterLayouts = (layout: Layout) => {
        if (isEditEnabled) {
            return layout.type === selectedLayoutType;
        }
        if (isTablet) {
            return layout.type === LayoutType.TABLET;
        }
        if (isMobileOnly) {
            return layout.type === LayoutType.MOBILE;
        }

        return true;
    };

    const getAllGridLayouts = () => {
        if (isSome(readLayoutsResult)) {
            return Object.keys(allGridLayouts).length > 0 ? allGridLayouts : asGridLayouts(readLayoutsResult.value, editingLayout);
        }
        return {};
    };
    const gridLayouts = useMemo(() => {
        if (isSome(readLayoutsResult)) {
            return Object.keys(allGridLayouts).length > 0 && editingLayout && isEditEnabled
                ? Object.fromEntries(Object.entries(allGridLayouts).filter(([key]) => key.includes(selectedLayoutType)))
                : asGridLayouts(readLayoutsResult.value.filter(filterLayouts), editingLayout);
        }
        return {};
    }, [readLayoutsResult, editingLayout, isEditEnabled, allGridLayouts, selectedLayoutType]);

    const widgetMap = useMemo(() => {
        if (widgets) return new Map(widgets.map((widget: Widget) => [widget.id, widget]));
        return new Map();
    }, [widgets]);

    const allGridLayoutsMemo = useMemo(() => getAllGridLayouts(), [widgets, readLayoutsResult, editingLayout, allGridLayouts]);

    useEffect(
        () => () => {
            if (resetWidgets) {
                resetWidgets();
            }
        },
        []
    );

    return {
        gridLayouts,
        widgetMap,
        allGridLayouts: allGridLayoutsMemo,
        setAllGridLayouts,
    };
};
const ViewSelectorContainer = (props: ViewSelectorContainerProps) => {
    const { dashboardId, isEditMode, isEditingLayout, disableRedirect } = props;
    const { data: readLayoutsResult, error, reset: readLayoutsReset, isLoading: isLoadingLayouts } = useReadLayouts(dashboardId);
    const { gridLayouts, allGridLayouts, widgetMap, setAllGridLayouts } = useGridLayoutsContainer(
        isEditingLayout,
        isEditMode,
        readLayoutsResult
    );
    const { navigationPush } = useNavigation();
    const { getResult } = useDashboard();
    const hasLayoutElements = Object.values(gridLayouts).every((arr) => arr.length > 0);
    const { updateError: updateLayoutsError, updateIsLoading: updateLayoutsIsLoading } = useLayouts();

    const { selectedLayoutType, layoutStatus } = useLayoutsView();
    const { setLayoutStatus } = dispatchLayoutsView();

    useEffect(() => {
        if (
            isSome(getResult) &&
            getResult.value.permittedActions.edit &&
            !hasLayoutElements &&
            getResult.value.id === dashboardId &&
            !isEditMode &&
            !updateLayoutsIsLoading &&
            !isLoadingLayouts &&
            !disableRedirect
        ) {
            navigationPush(`${getResult.value.id}/edit`);
        }
    }, [getResult, hasLayoutElements, dashboardId, updateLayoutsIsLoading, isLoadingLayouts, isEditMode]);

    useEffect(
        () => () => {
            readLayoutsReset();
        },
        []
    );

    useEffect(() => {
        if (isSome(updateLayoutsError) || layoutStatus === LAYOUT_STATUS.FORCE_RESET) {
            setAllGridLayouts({});
            if (layoutStatus === LAYOUT_STATUS.FORCE_RESET) {
                setLayoutStatus(LAYOUT_STATUS.READY);
            }
        }
    }, [updateLayoutsError, layoutStatus]);

    if (isSome(error)) {
        return (
            <Result
                variant={ResultVariants.ERROR}
                title={i18n.t('common.errorWrong')}
                info={i18n.t('common.errorWrongInfo')}
                icon='bot-dead'
            />
        );
    }

    if (updateLayoutsIsLoading || isLoadingLayouts) {
        return <Loading isLoading size={50} />;
    }
    if (!hasLayoutElements && isEditMode) {
        return <AddFirstWidgetResultContainer />;
    }
    if (!hasLayoutElements && !isEditMode) {
        return <EmptyDashboardPage />;
    }
    return (
        <GridContainer
            dashboardId={dashboardId}
            isEditEnabled={isEditMode}
            editingLayout={isEditingLayout}
            gridLayouts={gridLayouts}
            allGridLayouts={allGridLayouts}
            widgetMap={widgetMap}
            selectedLayoutType={selectedLayoutType}
            setAllGridLayouts={setAllGridLayouts}
        />
    );
};

const asGridLayouts = (layouts: Layout[], editable: boolean) => {
    const result: GridLayouts = {};
    layouts.forEach(
        (layout: Layout) =>
            (result[layout.type] = layout.widgets.map(
                (layoutElement: LayoutElement) =>
                    ({
                        i: layoutElement.id,
                        x: layoutElement.position.x,
                        y: layoutElement.position.y,
                        w: layoutElement.size.width,
                        h: layoutElement.size.height,
                        static: !editable,
                        maxHeight: layoutElement.constraints.maxHeight,
                        maxWidth: layoutElement.constraints.maxWidth,
                        minHeight: layoutElement.constraints.minHeight,
                        minWidth: layoutElement.constraints.minWidth,
                        type: layoutElement.type,
                    } as GridLayoutElement)
            ))
    );

    return result;
};

export default ViewSelectorContainer;
